import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { env } from '@/env'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import {
	fetchNotificationDetails,
	fetchNotificationPreferences,
	fetchNotifications,
	getOutboundNotification,
	listOutboundNotifications,
} from './api'

import type { NotificationsFilters } from './types'

export const notificationsKeys = {
	all: ['notifications'] as const,
	lists: () => [...notificationsKeys.all, 'list'] as const,
	list: (filters?: NotificationsFilters) =>
		[...notificationsKeys.lists(), filters, 'no-cache'] as const,
	details: () => [...notificationsKeys.all, 'details'] as const,
	detail: (notificationId: string) =>
		[...notificationsKeys.details(), notificationId] as const,
	preferences: () => [...notificationsKeys.all, 'preferences'] as const,
	outbound: (tenant: string) =>
		[...notificationsKeys.all, 'outbound', tenant] as const,
	outboundDetail: (tenant: string, id: string) =>
		[...notificationsKeys.outbound(tenant), id] as const,
}

export const notificationsQuery = {
	list: (filters?: NotificationsFilters) =>
		queryOptions({
			queryKey: notificationsKeys.list(filters),
			queryFn: ({ signal }) => fetchNotifications(filters, signal),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			gcTime: time(2, 'm'),
			refetchInterval: env.VITE_DISABLE_NOTIFICATIONS ? false : time(30, 's'),
			enabled: !env.VITE_DISABLE_NOTIFICATIONS,
		}),

	detail: (notificationId: string, enabled: boolean = false) =>
		queryOptions({
			queryKey: notificationsKeys.detail(notificationId),
			queryFn: ({ signal }) => fetchNotificationDetails(notificationId, signal),
			staleTime: ({ state }) => successStaleTime(state, 5, 'm'),
			gcTime: time(10, 'm'),
			enabled,
		}),

	preferences: () =>
		queryOptions({
			queryKey: notificationsKeys.preferences(),
			queryFn: ({ signal }) => fetchNotificationPreferences(signal),
			staleTime: ({ state }) => successStaleTime(state, 5, 'm'),
			gcTime: time(10, 'm'),
			meta: { tenantAgnostic: true },
		}),

	outbound: (tenant: string) =>
		queryOptions({
			queryKey: notificationsKeys.outbound(tenant),
			queryFn: ({ signal }) => listOutboundNotifications(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 5, 'm'),
			gcTime: time(10, 'm'),
		}),

	outboundDetail: (tenant: string, id: string) =>
		queryOptions({
			queryKey: notificationsKeys.outboundDetail(tenant, id),
			queryFn: ({ signal }) => getOutboundNotification(tenant, id, signal),
			staleTime: ({ state }) => successStaleTime(state, 5, 'm'),
			gcTime: time(10, 'm'),
		}),
}

/**
 * Fetch the current user's notifications
 * @param {NotificationsFilters} filters - Filters to apply to the notifications
 */
export const useNotifications = (filters?: NotificationsFilters) => {
	return useSuspenseQuery(notificationsQuery.list(filters))
}

/**
 * Fetch the current user's notification details
 * @param {string} notificationId - The ID of the notification to fetch
 * @param {boolean} enabled - Whether the query should be enabled
 */
export const useNotificationDetails = (
	notificationId: string,
	enabled: boolean = false,
) => {
	return useQuery(notificationsQuery.detail(notificationId, enabled))
}

/**
 * Fetch the current user's notification preferences across all tenants.
 */
export const useNotificationPreferences = () => {
	return useSuspenseQuery(notificationsQuery.preferences())
}

/**
 * Fetch the current tenant's outbound notification recipients
 */
export const useOutboundNotifications = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(notificationsQuery.outbound(tenant))
}

/**
 * Fetch a single outbound notification recipient for the current tenant
 * @param {string} id - The outbound notification UUID
 */
export const useOutboundNotification = (id: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(notificationsQuery.outboundDetail(tenant, id))
}
